import React, { useContext, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import {
  Alert,
  Col,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ApexOptions, ApexTooltipCustomOpts } from 'apexcharts';

import ProjectsDashboardContext from '@totem/components/ticketing/projectsDashboard/ProjectsDashboardContext';
import {
  HygieneRollup,
  PMWorkloadEntry,
  ProjectHealthSummary,
} from '@totem/components/ticketing/projectsDashboard/types';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import ContentLayout from '@totem/components/ui_components/page/ContentLayout';

const { Text } = Typography;

interface KpiCardProps {
  label: string;
  value: React.ReactNode;
  sub: string;
  valueColor?: string;
}

const KpiCard = ({ label, value, sub, valueColor }: KpiCardProps) => (
  <div
    style={{
      background: 'var(--ant-color-bg-container)',
      border: '1px solid var(--ant-color-border)',
      borderRadius: 10,
      padding: '12px 14px',
      boxShadow: '0 1px 2px rgba(0,0,0,.05), 0 4px 12px rgba(0,0,0,.04)',
      minWidth: 0,
    }}
  >
    <div
      style={{
        fontSize: 10.5,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'var(--ant-color-text-secondary)',
        fontWeight: 600,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 26,
        fontWeight: 700,
        color: valueColor ?? 'var(--ant-color-text)',
        marginTop: 4,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </div>
    <div
      style={{
        fontSize: 11,
        color: 'var(--ant-color-text-secondary)',
        marginTop: 6,
      }}
    >
      {sub}
    </div>
  </div>
);

const HEALTH_COLOR: Record<string, string> = {
  red: '#ff4d4f',
  yellow: '#faad14',
  green: '#52c41a',
};

const HEALTH_LABEL: Record<string, string> = {
  red: 'At Risk',
  yellow: 'Watch',
  green: 'On Track',
};

const SEVERITY_COLOR: Record<string, string> = {
  high: '#ff4d4f',
  med: '#faad14',
  low: '#1677ff',
};

const formatCurrency = (val: number) =>
  val === 0
    ? '—'
    : `$${val.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const HealthBadge = ({ health }: { health: string }) => (
  <Tag
    color={HEALTH_COLOR[health]}
    style={{ minWidth: 72, textAlign: 'center' }}
  >
    {HEALTH_LABEL[health] ?? health}
  </Tag>
);

// Distinct palette for AM series (matches original report's colour order)
const AM_PALETTE = [
  '#22c55e',
  '#E8540A',
  '#a855f7',
  '#0ea5e9',
  '#3b82f6',
  '#f59e0b',
  '#64748b',
  '#ec4899',
];

// ── Summary Tab ───────────────────────────────────────────────────────────────

const SummaryTab = ({ projects }: { projects: ProjectHealthSummary[] }) => {
  const red = projects.filter((proj) => proj.health === 'red').length;
  const yellow = projects.filter((proj) => proj.health === 'yellow').length;
  const green = projects.filter((proj) => proj.health === 'green').length;
  const total = projects.length;
  const totalFee = projects.reduce((acc, proj) => acc + proj.fee, 0);
  const totalBudgetedHours = projects.reduce(
    (acc, proj) => acc + proj.budgetedHours,
    0,
  );
  const totalActualHours = projects.reduce(
    (acc, proj) => acc + proj.actualHours,
    0,
  );
  const overallBurnPct =
    totalBudgetedHours > 0
      ? Math.round((totalActualHours / totalBudgetedHours) * 100)
      : 0;
  const zeroTasks = projects.filter((proj) => proj.totalTasks === 0).length;

  // Per-AM aggregated stats, sorted by fee desc
  const amStats = useMemo(() => {
    const groups: Record<
      string,
      {
        red: number;
        yellow: number;
        green: number;
        fee: number;
        budgetedHours: number;
        actualHours: number;
        totalTasks: number;
        doneTasks: number;
        projects: number;
      }
    > = {};
    for (const proj of projects) {
      const key = proj.am || 'Unassigned';
      if (!groups[key]) {
        groups[key] = {
          red: 0,
          yellow: 0,
          green: 0,
          fee: 0,
          budgetedHours: 0,
          actualHours: 0,
          totalTasks: 0,
          doneTasks: 0,
          projects: 0,
        };
      }
      groups[key][proj.health as 'red' | 'yellow' | 'green']++;

      groups[key].fee += proj.fee;

      groups[key].budgetedHours += proj.budgetedHours;

      groups[key].actualHours += proj.actualHours;

      groups[key].totalTasks += proj.totalTasks;

      groups[key].doneTasks += proj.completedTasks;

      groups[key].projects++;
    }
    return Object.entries(groups).sort(
      (entA, entB) => entB[1].fee - entA[1].fee,
    );
  }, [projects]);

  const amNames = amStats.map(([name]) => name);

  // Scatter chart: x = % tasks complete (0–100), y = hours burn %, bubble size = fee
  // Bubble radius uses sqrt(fee)/sqrt(maxFee)*30, clamped 4–30, matching the original report.
  const scatterSeries = useMemo(() => {
    const maxFee = Math.max(...projects.map((proj) => proj.fee), 1);
    return amNames.map((amName) => ({
      name: amName,
      data: projects
        .filter((proj) => (proj.am || 'Unassigned') === amName)
        .map((proj) => ({
          x: proj.pctTasksDone,
          y: Math.min(proj.burnPct, 300),
          // eslint-disable-next-line id-length
          z:
            proj.fee > 0
              ? Math.max(
                  8,
                  Math.min(60, (Math.sqrt(proj.fee) / Math.sqrt(maxFee)) * 60),
                )
              : 8,
          fee: proj.fee,
        })),
    }));
  }, [projects, amNames]);

  // Draw the diagonal reference line (0,0)→(100,100) and "over budget" band directly on
  // the ApexCharts SVG because mixed bubble+line isn't a supported combination.
  const drawScatterOverlays = (chartCtx: any) => {
    const globals = chartCtx.w.globals;
    const { gridWidth, gridHeight, translateX, translateY, maxY } = globals;
    if (!gridWidth || !gridHeight) return;

    const yMax = maxY || 300;

    // Map data coords → SVG pixel coords
    const toX = (val: number) => translateX + (val / 100) * gridWidth;
    const toY = (val: number) =>
      translateY + gridHeight - (val / yMax) * gridHeight;

    const svgEl: SVGElement | null =
      chartCtx.el.querySelector('.apexcharts-svg');
    if (!svgEl) return;

    // Remove previous overlays so updates don't stack
    svgEl
      .querySelectorAll('.scatter-overlay')
      .forEach((el: Element) => el.remove());

    const ns = 'http://www.w3.org/2000/svg';

    // Red band: fill above y=100 (over-budget zone)
    const bandY = toY(100);
    const bandTop = toY(yMax);
    if (bandY > bandTop) {
      const rect = document.createElementNS(ns, 'rect');
      rect.setAttribute('class', 'scatter-overlay');

      rect.setAttribute('x', String(translateX));

      rect.setAttribute('y', String(bandTop));

      rect.setAttribute('width', String(gridWidth));

      rect.setAttribute('height', String(bandY - bandTop));

      rect.setAttribute('fill', '#ff4d4f');

      rect.setAttribute('fill-opacity', '0.08');

      rect.setAttribute('pointer-events', 'none');

      svgEl.appendChild(rect);
    }

    // Dashed diagonal reference line from (0,0) to (100,100)
    const line = document.createElementNS(ns, 'line');
    line.setAttribute('class', 'scatter-overlay');

    line.setAttribute('x1', String(toX(0)));

    line.setAttribute('y1', String(toY(0)));

    line.setAttribute('x2', String(toX(100)));

    line.setAttribute('y2', String(toY(100)));

    line.setAttribute('stroke', '#94a3b8');

    line.setAttribute('stroke-width', '1.5');

    line.setAttribute('stroke-dasharray', '6 4');

    line.setAttribute('pointer-events', 'none');

    svgEl.appendChild(line);
  };

  const scatterOptions: ApexOptions = {
    chart: {
      type: 'bubble',
      toolbar: { show: false },
      animations: { enabled: false },
      events: { mounted: drawScatterOverlays, updated: drawScatterOverlays },
    },
    dataLabels: { enabled: false },
    plotOptions: {
      bubble: { minBubbleRadius: 8, maxBubbleRadius: 60 },
    },
    colors: AM_PALETTE,
    annotations: {
      yaxis: [
        {
          y: 100,
          label: {
            text: 'Over Budget',
            style: {
              color: '#ff4d4f',
              background: 'transparent',
              fontSize: '10px',
            },
          },
        },
      ],
    },
    xaxis: {
      type: 'numeric',
      title: { text: '% Tasks Complete' },
      min: -10,
      max: 110,
      tickAmount: 12,
      labels: {
        formatter: (val: string) => {
          const num = Number(val);
          return num < 0 || num > 100 ? '' : `${num.toFixed(0)}%`;
        },
      },
    },
    yaxis: {
      title: { text: 'Hours Burn %' },
      min: -20,
      labels: {
        formatter: (val: number) => (val < 0 ? '' : `${val.toFixed(0)}%`),
      },
    },
    tooltip: {
      custom: ({
        seriesIndex,
        dataPointIndex,
        w, // eslint-disable-line id-length
      }: ApexTooltipCustomOpts) => {
        const series = w.config.series as {
          name: string;
          data: { x: number; y: number; z: number; fee: number }[];
        }[];
        const pt = series[seriesIndex].data[dataPointIndex];
        return `<div style="padding:8px 12px;font-size:12px">
          <strong>${series[seriesIndex].name}</strong><br/>
          Done: ${pt.x.toFixed(0)}% · Burn: ${pt.y.toFixed(0)}%<br/>
          Fee: $${(pt.fee ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
        </div>`;
      },
    },
    legend: { show: true, position: 'bottom' },
    grid: { borderColor: 'var(--ant-color-border)' },
  };

  // Horizontal stacked bar: On Track → Watch → At Risk (left to right = good to bad)
  const barSeries = [
    {
      name: 'On Track',
      data: amNames.map(
        (name) => amStats.find(([amName]) => amName === name)![1].green,
      ),
    },
    {
      name: 'Watch',
      data: amNames.map(
        (name) => amStats.find(([amName]) => amName === name)![1].yellow,
      ),
    },
    {
      name: 'At Risk',
      data: amNames.map(
        (name) => amStats.find(([amName]) => amName === name)![1].red,
      ),
    },
  ];

  const barOptions: ApexOptions = {
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: { show: false },
      animations: { enabled: false },
    },
    plotOptions: { bar: { horizontal: true, barHeight: '60%' } },
    colors: [HEALTH_COLOR.green, HEALTH_COLOR.yellow, HEALTH_COLOR.red],
    xaxis: {
      categories: amNames,
      labels: { formatter: (val: string) => `${val}` },
    },
    yaxis: { labels: { style: { fontSize: '11px' } } },
    legend: { show: true, position: 'bottom' },
    grid: { borderColor: 'var(--ant-color-border)' },
    tooltip: { shared: true, intersect: false },
    dataLabels: { enabled: false },
  };

  // Mini inline burn-% bar (matches original report style)
  const BurnBar = ({ pct, budgeted }: { pct: number; budgeted: number }) => {
    if (budgeted === 0) {
      return <Text type="secondary">—</Text>;
    }
    const capped = Math.min(pct, 100);
    const barColor =
      pct > 110 ? HEALTH_COLOR.red : pct > 80 ? HEALTH_COLOR.yellow : '#22c55e';
    return (
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 110 }}
      >
        <div
          style={{
            flex: 1,
            height: 8,
            background: 'var(--ant-color-fill-tertiary)',
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${capped}%`,
              height: '100%',
              background: barColor,
              borderRadius: 4,
            }}
          />
        </div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            minWidth: 36,
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {pct.toFixed(0)}%
        </span>
      </div>
    );
  };

  type AmScorecardRow = {
    key: string;
    am: string;
    red: number;
    yellow: number;
    green: number;
    fee: number;
    budgetedHours: number;
    actualHours: number;
    totalTasks: number;
    doneTasks: number;
    projects: number;
  };

  const amScorecardColumns: ColumnsType<AmScorecardRow> = [
    {
      title: 'Account Manager',
      dataIndex: 'am',
      key: 'am',
      render: (val: string) => <strong>{val}</strong>,
    },
    {
      title: 'Projects',
      dataIndex: 'projects',
      key: 'projects',
      align: 'right' as const,
    },
    {
      title: 'Fee in Flight',
      dataIndex: 'fee',
      key: 'fee',
      align: 'right' as const,
      render: (val: number) => formatCurrency(val),
    },
    {
      title: 'Budgeted Hrs',
      dataIndex: 'budgetedHours',
      key: 'budgetedHours',
      align: 'right' as const,
      render: (val: number) => (val === 0 ? '—' : val.toFixed(0)),
    },
    {
      title: 'Actual Hrs',
      dataIndex: 'actualHours',
      key: 'actualHours',
      align: 'right' as const,
      render: (val: number) => (val === 0 ? '—' : val.toFixed(1)),
    },
    {
      title: 'Burn %',
      key: 'burnPct',
      render: (_: unknown, row: AmScorecardRow) => {
        const pct =
          row.budgetedHours > 0
            ? (row.actualHours / row.budgetedHours) * 100
            : 0;
        return <BurnBar pct={pct} budgeted={row.budgetedHours} />;
      },
    },
    {
      title: '% Tasks Complete',
      key: 'pctTasksDone',
      render: (_: unknown, row: AmScorecardRow) => {
        if (row.totalTasks === 0) return <Text type="secondary">—</Text>;
        const pct = Math.round((row.doneTasks / row.totalTasks) * 100);
        return <BurnBar pct={pct} budgeted={row.totalTasks} />;
      },
    },
    {
      title: 'Health Mix (R/Y/G)',
      key: 'health',
      render: (_: unknown, row: AmScorecardRow) => (
        <Space size={4}>
          <Tag
            color={HEALTH_COLOR.red}
            style={{ margin: 0, minWidth: 24, textAlign: 'center' }}
          >
            {row.red}
          </Tag>
          <Tag
            color={HEALTH_COLOR.yellow}
            style={{ margin: 0, minWidth: 24, textAlign: 'center' }}
          >
            {row.yellow}
          </Tag>
          <Tag
            color={HEALTH_COLOR.green}
            style={{ margin: 0, minWidth: 24, textAlign: 'center' }}
          >
            {row.green}
          </Tag>
        </Space>
      ),
    },
  ];

  const amScorecardRows: AmScorecardRow[] = amStats.map(([am, stats]) => ({
    key: am,
    am,
    ...stats,
  }));

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="large">
      {/* KPI row */}
      <Row gutter={[12, 12]}>
        <Col xs={12} sm={5}>
          <KpiCard
            label="Active Open Projects"
            value={total}
            sub={`Across ${amNames.length} Account Managers`}
          />
        </Col>
        <Col xs={12} sm={5}>
          <KpiCard
            label="Contract Value in Flight"
            value={formatCurrency(totalFee)}
            sub="Sum of project.fee"
          />
        </Col>
        <Col xs={12} sm={5}>
          <KpiCard
            label="Hours Burn (Actual / Budgeted)"
            value={`${overallBurnPct}%`}
            sub={`${totalActualHours.toFixed(1)} / ${totalBudgetedHours.toFixed(0)} hrs`}
          />
        </Col>
        <Col xs={12} sm={4}>
          <KpiCard
            label="Setup Incomplete"
            value={zeroTasks}
            sub="Projects with zero tasks"
            valueColor={zeroTasks > 0 ? HEALTH_COLOR.red : undefined}
          />
        </Col>
        <Col xs={12} sm={5}>
          <KpiCard
            label="At-Risk Projects"
            value={
              <>
                <span
                  style={{
                    color: red > 0 ? HEALTH_COLOR.red : 'var(--ant-color-text)',
                  }}
                >
                  {red}
                </span>
                {yellow > 0 && (
                  <span
                    style={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: HEALTH_COLOR.yellow,
                      marginLeft: 8,
                    }}
                  >
                    +{yellow} watch
                  </span>
                )}
              </>
            }
            sub={`${green} on track`}
          />
        </Col>
      </Row>

      {/* Charts row */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <IBCard
            isCovered
            title="Burn-Rate Scatter — Hours Used vs Tasks Done"
          >
            <ReactApexChart
              type="bubble"
              series={scatterSeries}
              options={scatterOptions}
              height={300}
            />
          </IBCard>
        </Col>
        <Col xs={24} lg={12}>
          <IBCard isCovered title="Project Count by AM — Health Stack">
            <ReactApexChart
              type="bar"
              series={barSeries}
              options={barOptions}
              height={300}
            />
          </IBCard>
        </Col>
      </Row>

      {/* AM Scorecard */}
      <IBCard
        isCovered
        title={
          <span>
            Account Manager Scorecard{' '}
            <Text type="secondary" style={{ fontSize: 11, fontWeight: 400 }}>
              one row per AM · projects, fee, burn, health mix
            </Text>
          </span>
        }
      >
        <Table
          dataSource={amScorecardRows}
          columns={amScorecardColumns}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </IBCard>
    </Space>
  );
};

// ── Mini progress bar for % columns ──────────────────────────────────────────

const PctBar = ({ pct, color }: { pct: number; color: string }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 100 }}>
    <div
      style={{
        flex: 1,
        height: 7,
        background: 'var(--ant-color-fill-tertiary)',
        borderRadius: 4,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${Math.min(pct, 100)}%`,
          height: '100%',
          background: color,
          borderRadius: 4,
        }}
      />
    </div>
    <span
      style={{
        fontSize: 11,
        fontVariantNumeric: 'tabular-nums',
        minWidth: 30,
        textAlign: 'right',
      }}
    >
      {pct.toFixed(0)}%
    </span>
  </div>
);

// ── Projects Tab ──────────────────────────────────────────────────────────────

const ProjectsTab = ({ projects }: { projects: ProjectHealthSummary[] }) => {
  const [pageSize, setPageSize] = useState(25);
  const [filterServiceLine, setFilterServiceLine] = useState('');
  const [filterActivity, setFilterActivity] = useState('');
  const [filterHealth, setFilterHealth] = useState('');

  const serviceLines = useMemo(
    () =>
      Array.from(
        new Set(projects.map((proj) => proj.category).filter(Boolean)),
      ).sort(),
    [projects],
  );
  const activities = useMemo(
    () =>
      Array.from(
        new Set(projects.map((proj) => proj.subCategory).filter(Boolean)),
      ).sort(),
    [projects],
  );
  const filtered = useMemo(
    () =>
      projects.filter(
        (proj) =>
          (!filterServiceLine || proj.category === filterServiceLine) &&
          (!filterActivity || proj.subCategory === filterActivity) &&
          (!filterHealth || proj.health === filterHealth),
      ),
    [projects, filterServiceLine, filterActivity, filterHealth],
  );

  const columns: ColumnsType<ProjectHealthSummary> = [
    {
      title: 'Health',
      dataIndex: 'health',
      key: 'health',
      width: 86,
      fixed: 'left',
      render: (val: string) => <HealthBadge health={val} />,
    },
    {
      title: 'Project',
      key: 'project',
      width: 180,
      ellipsis: true,
      render: (_: unknown, record: ProjectHealthSummary) => (
        <>
          <a
            href={`https://dashboard.ib-services.net/dashboard/ticket/${record.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontWeight: 600 }}
          >
            {record.number}
          </a>
          <div
            style={{
              fontSize: 11,
              color: 'var(--ant-color-text-secondary)',
              marginTop: 1,
            }}
          >
            {record.summary}
          </div>
        </>
      ),
    },
    {
      title: 'Customer / Building',
      key: 'customer',
      ellipsis: true,
      render: (_: unknown, record: ProjectHealthSummary) => (
        <>
          <div>{record.orgName}</div>
          {record.buildingName && (
            <div
              style={{ fontSize: 11, color: 'var(--ant-color-text-secondary)' }}
            >
              {record.buildingName}
            </div>
          )}
        </>
      ),
    },
    {
      title: 'Service / Activity',
      key: 'service',
      ellipsis: true,
      render: (_: unknown, record: ProjectHealthSummary) => (
        <>
          <div>{record.category || <Text type="secondary">—</Text>}</div>
          {record.subCategory && (
            <div
              style={{ fontSize: 11, color: 'var(--ant-color-text-secondary)' }}
            >
              {record.subCategory}
            </div>
          )}
        </>
      ),
    },
    {
      title: 'Account Manager',
      dataIndex: 'am',
      key: 'am',
      render: (val: string) => val || <Text type="secondary">Unassigned</Text>,
    },
    {
      title: 'Project Manager(s)',
      dataIndex: 'pmNames',
      key: 'pmNames',
      render: (names: string[] | null) =>
        names && names.length > 0 ? (
          names.join(', ')
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Age',
      key: 'ageDays',
      align: 'right',
      width: 70,
      render: (_: unknown, record: ProjectHealthSummary) => (
        <span
          style={{
            fontVariantNumeric: 'tabular-nums',
            fontWeight: record.health !== 'green' ? 600 : undefined,
            color: HEALTH_COLOR[record.health] ?? undefined,
          }}
        >
          {record.ageDays}d
        </span>
      ),
      sorter: (rowA, rowB) => rowA.ageDays - rowB.ageDays,
    },
    {
      title: 'Last Move',
      dataIndex: 'daysSinceMovement',
      key: 'daysSinceMovement',
      align: 'right',
      width: 85,
      render: (val: number | null) =>
        val === null ? <Text type="secondary">—</Text> : `${val}d ago`,
      sorter: (rowA, rowB) =>
        (rowA.daysSinceMovement ?? 9999) - (rowB.daysSinceMovement ?? 9999),
    },
    {
      title: 'Sub-Status',
      dataIndex: 'subStatus',
      key: 'subStatus',
      render: (val: string) => val || <Text type="secondary">—</Text>,
    },
    {
      title: 'Tasks',
      key: 'tasks',
      align: 'right',
      width: 80,
      render: (_: unknown, record: ProjectHealthSummary) =>
        record.totalTasks === 0 ? (
          <Text type="secondary">—</Text>
        ) : (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {record.completedTasks}/{record.totalTasks}
          </span>
        ),
      sorter: (rowA, rowB) => rowA.totalTasks - rowB.totalTasks,
    },
    {
      title: '% Complete (Tasks)',
      dataIndex: 'pctTasksDone',
      key: 'pctTasksDone',
      width: 150,
      render: (val: number) => <PctBar pct={val} color="#1677ff" />,
      sorter: (rowA, rowB) => rowA.pctTasksDone - rowB.pctTasksDone,
    },
    {
      title: '% Complete (Hours)',
      dataIndex: 'burnPct',
      key: 'burnPct',
      width: 150,
      render: (val: number) => (
        <PctBar
          pct={val}
          color={
            val > 110
              ? HEALTH_COLOR.red
              : val > 80
                ? HEALTH_COLOR.yellow
                : '#22c55e'
          }
        />
      ),
      sorter: (rowA, rowB) => rowA.burnPct - rowB.burnPct,
    },
    {
      title: 'Hours Act/Bud',
      key: 'hours',
      align: 'right',
      width: 110,
      render: (_: unknown, record: ProjectHealthSummary) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {record.actualHours.toFixed(1)} /{' '}
          {record.budgetedHours === 0 ? '—' : record.budgetedHours.toFixed(0)}
        </span>
      ),
      sorter: (rowA, rowB) => rowA.actualHours - rowB.actualHours,
    },
    {
      title: 'Fee',
      dataIndex: 'fee',
      key: 'fee',
      align: 'right',
      render: (val: number) => formatCurrency(val),
      sorter: (rowA, rowB) => rowA.fee - rowB.fee,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Flags',
      dataIndex: 'flags',
      key: 'flags',
      render: (flags: string[] | null) =>
        !flags || flags.length === 0 ? null : (
          <Space size={2} wrap>
            {flags.map((flag) => (
              <Tag
                key={flag}
                color="warning"
                style={{ margin: 0, fontSize: 10, padding: '0 5px' }}
              >
                {flag}
              </Tag>
            ))}
          </Space>
        ),
    },
  ];

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="small">
      {/* Filter bar */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          alignItems: 'center',
          padding: '10px 14px',
          background: 'var(--ant-color-fill-quaternary)',
          borderRadius: 6,
          border: '1px solid var(--ant-color-border)',
        }}
      >
        <Select
          placeholder="All Service Lines"
          style={{ minWidth: 200 }}
          value={filterServiceLine || undefined}
          allowClear
          onChange={(val) => setFilterServiceLine(val ?? '')}
          options={serviceLines.map((val) => ({
            value: val,
            label: `${val} (${projects.filter((proj) => proj.category === val).length})`,
          }))}
        />
        <Select
          placeholder="All Activities"
          style={{ minWidth: 180 }}
          value={filterActivity || undefined}
          allowClear
          onChange={(val) => setFilterActivity(val ?? '')}
          options={activities.map((val) => ({
            value: val,
            label: `${val} (${projects.filter((proj) => proj.subCategory === val).length})`,
          }))}
        />
        <Select
          placeholder="All Health"
          style={{ minWidth: 140 }}
          value={filterHealth || undefined}
          allowClear
          onChange={(val) => setFilterHealth(val ?? '')}
          options={[
            { value: 'red', label: 'At Risk' },
            { value: 'yellow', label: 'Watch' },
            { value: 'green', label: 'On Track' },
          ]}
        />
        {(filterServiceLine || filterActivity || filterHealth) && (
          <a
            onClick={() => {
              setFilterServiceLine('');

              setFilterActivity('');

              setFilterHealth('');
            }}
            style={{ fontSize: 12 }}
          >
            Clear all
          </a>
        )}
        <Text type="secondary" style={{ fontSize: 12, marginLeft: 'auto' }}>
          Showing {filtered.length} of {projects.length} projects
        </Text>
      </div>

      <Table<ProjectHealthSummary>
        dataSource={filtered}
        columns={columns}
        rowKey="id"
        size="small"
        pagination={{
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '25', '50', '100'],
          total: filtered.length,
          showTotal: (total) => `${total} projects`,
          onShowSizeChange: (_current, size) => setPageSize(size),
        }}
        scroll={{ x: 'max-content' }}
      />
    </Space>
  );
};

// ── Hygiene Tab ───────────────────────────────────────────────────────────────

// Metadata for known hygiene flag codes — keys are snake_case to match backend flag codes
/* eslint-disable camelcase */
const HYGIENE_FLAG_META: Record<
  string,
  { title: string; sub: string; severity: 'high' | 'med' | 'low' }
> = {
  no_am: {
    title: 'No AM',
    sub: 'no Account Manager assigned',
    severity: 'high',
  },
  no_pm: {
    title: 'No PM',
    sub: 'no active Project Manager assigned',
    severity: 'high',
  },
  no_start: {
    title: 'No start date',
    sub: 'project missing a start date',
    severity: 'med',
  },
  no_end: {
    title: 'No end date',
    sub: 'project missing a target close date',
    severity: 'med',
  },
  no_budget: {
    title: 'No budget',
    sub: 'no budgeted hours set',
    severity: 'med',
  },
  no_fee: { title: 'No fee', sub: 'no fee amount set', severity: 'med' },
  no_building: {
    title: 'No building',
    sub: 'not associated with a building',
    severity: 'low',
  },
  no_tasks: {
    title: 'Zero tasks',
    sub: 'no task plan defined',
    severity: 'high',
  },
  no_milestones: {
    title: 'No billing milestones',
    sub: 'no billing milestone tasks found',
    severity: 'low',
  },
  blank_summary: {
    title: 'Blank/short summary',
    sub: 'project title is missing or too short',
    severity: 'low',
  },
  overdue: {
    title: 'Past end date',
    sub: 'end date passed, still open',
    severity: 'high',
  },
  ready_to_close: {
    title: 'Ready to close',
    sub: 'all tasks done, no open items',
    severity: 'med',
  },
  never_started: {
    title: 'Never started',
    sub: 'old project with no tasks logged',
    severity: 'high',
  },
  status_drift: {
    title: 'Status drift',
    sub: 'status inconsistent with tasks',
    severity: 'med',
  },
  date_inversion: {
    title: 'Date inversion',
    sub: 'end date is before start date',
    severity: 'high',
  },
  burn_no_progress: {
    title: 'Burn w/ no progress',
    sub: 'hours logged but no tasks moved',
    severity: 'high',
  },
  hours_mismatch: {
    title: 'Hours mismatch',
    sub: 'task hours inconsistent with budget',
    severity: 'med',
  },
  orphan_tasks: {
    title: 'Orphan tasks',
    sub: 'tasks with invalid parent references',
    severity: 'low',
  },
  stale_60: {
    title: 'Stale ≥60d',
    sub: 'no movement in 60+ days (excl. FTE)',
    severity: 'high',
  },
  very_old: { title: 'Very old', sub: 'open for 1+ year', severity: 'med' },
  burn_ahead: {
    title: 'Burn ahead of calendar',
    sub: 'hours run hot vs schedule',
    severity: 'med',
  },
  burn_behind: {
    title: 'Burn behind calendar',
    sub: 'hours lag behind schedule',
    severity: 'med',
  },
  zombie: { title: 'Zombie', sub: 'stale + no PM assigned', severity: 'high' },
};
/* eslint-enable camelcase */

const SEVERITY_SORT: Record<string, number> = { high: 0, med: 1, low: 2 };

interface HygieneCardBucket {
  code: string;
  title: string;
  sub: string;
  severity: 'high' | 'med' | 'low';
  projects: ProjectHealthSummary[];
}

const HygieneItemMeta = ({
  project,
  flagCode,
}: {
  project: ProjectHealthSummary;
  flagCode: string;
}) => {
  if (flagCode === 'stale_60' || flagCode === 'zombie') {
    const dsm = project.daysSinceMovement;
    if (dsm !== null && dsm !== undefined) return <>{dsm}d since last move</>;
  }
  if (flagCode === 'overdue') {
    if (project.endDate) {
      const daysOver = Math.round(
        (Date.now() - new Date(project.endDate).getTime()) / 86400000,
      );
      return <>{daysOver}d past end</>;
    }
  }
  return <>{project.ageDays}d old</>;
};

const HygieneCard = ({ bucket }: { bucket: HygieneCardBucket }) => {
  const borderColor =
    bucket.severity === 'high'
      ? SEVERITY_COLOR.high
      : bucket.severity === 'med'
        ? SEVERITY_COLOR.med
        : 'var(--ant-color-border)';

  return (
    <div
      style={{
        background: 'var(--ant-color-bg-container)',
        border: `1px solid var(--ant-color-border)`,
        borderRadius: 8,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Header — dark navy background matching original report */}
      <div
        style={{
          background: '#122048',
          borderRadius: '7px 7px 0 0',
          padding: '10px 14px 8px',
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 14, color: '#fff' }}>
          {bucket.title}
        </span>
        <span
          style={{
            background: borderColor,
            color: '#fff',
            borderRadius: 10,
            padding: '1px 7px',
            fontSize: 12,
            fontWeight: 700,
            lineHeight: '18px',
          }}
        >
          {bucket.projects.length}
        </span>
        <span
          style={{
            fontSize: 11,
            color: 'rgba(255,255,255,0.65)',
            flexBasis: '100%',
            marginTop: -4,
          }}
        >
          {bucket.sub}
        </span>
      </div>
      {/* Project list */}
      <div style={{ overflowY: 'auto', maxHeight: 340, padding: '6px 0' }}>
        {bucket.projects.map((proj) => (
          <div
            key={proj.id}
            style={{
              padding: '6px 14px',
              borderBottom: '1px solid var(--ant-color-border-secondary)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 8,
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ marginBottom: 2 }}>
                <a
                  href={`https://dashboard.ib-services.net/dashboard/ticket/${proj.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontWeight: 600, fontSize: 12 }}
                >
                  {proj.number}
                </a>
                <span
                  style={{
                    marginLeft: 6,
                    fontSize: 12,
                    color: 'var(--ant-color-text)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'inline-block',
                    maxWidth: 260,
                    verticalAlign: 'middle',
                  }}
                >
                  {proj.summary}
                </span>
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--ant-color-text-secondary)',
                  lineHeight: 1.4,
                }}
              >
                {proj.orgName}
                {proj.buildingName ? ` · ${proj.buildingName}` : ''}
                {proj.am ? ` · AM: ${proj.am}` : ''}
                {proj.pmNames.length > 0
                  ? ` · PM: ${proj.pmNames.join(', ')}`
                  : ''}
              </div>
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--ant-color-text-secondary)',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                paddingTop: 2,
              }}
            >
              <HygieneItemMeta project={proj} flagCode={bucket.code} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const HygieneTab = ({
  projects,
  pmWorkload,
  hygieneRollup: _hygieneRollup,
}: {
  projects: ProjectHealthSummary[];
  pmWorkload: Record<string, PMWorkloadEntry>;
  hygieneRollup: HygieneRollup;
}) => {
  // Group projects by each hygiene flag code they carry
  const buckets = useMemo(() => {
    const byCode = new Map<string, ProjectHealthSummary[]>();
    for (const proj of projects) {
      const seen = new Set<string>();
      for (const flag of proj.hygiene.flags) {
        if (!seen.has(flag.code)) {
          seen.add(flag.code);
          if (!byCode.has(flag.code)) byCode.set(flag.code, []);
          byCode.get(flag.code)!.push(proj);
        }
      }
    }
    const result: HygieneCardBucket[] = [];
    for (const [code, projs] of byCode.entries()) {
      const meta = HYGIENE_FLAG_META[code] ?? {
        title: code.replace(/_/g, ' '),
        sub: '',
        severity: 'low' as const,
      };
      result.push({ code, ...meta, projects: projs });
    }
    result.sort(
      (aItem, bItem) =>
        SEVERITY_SORT[aItem.severity] - SEVERITY_SORT[bItem.severity] ||
        bItem.projects.length - aItem.projects.length,
    );
    return result;
  }, [projects]);

  const pmColumns: ColumnsType<{ name: string } & PMWorkloadEntry> = [
    { title: 'PM', dataIndex: 'name', key: 'name' },
    { title: 'Projects', dataIndex: 'projects', key: 'projects' },
    {
      title: 'At Risk',
      dataIndex: 'red',
      key: 'red',
      render: (val: number) => (
        <Text style={{ color: HEALTH_COLOR.red }}>{val}</Text>
      ),
    },
    {
      title: 'Watch',
      dataIndex: 'yellow',
      key: 'yellow',
      render: (val: number) => (
        <Text style={{ color: HEALTH_COLOR.yellow }}>{val}</Text>
      ),
    },
    {
      title: 'On Track',
      dataIndex: 'green',
      key: 'green',
      render: (val: number) => (
        <Text style={{ color: HEALTH_COLOR.green }}>{val}</Text>
      ),
    },
    {
      title: 'Avg Hygiene',
      dataIndex: 'hygieneAvg',
      key: 'hygieneAvg',
      render: (val: number) => val.toFixed(1),
    },
  ];

  const pmRows = Object.entries(pmWorkload).map(([name, entry]) => ({
    name,
    ...entry,
    key: name,
  }));

  const totalFlagged = useMemo(
    () =>
      new Set(
        projects.flatMap((proj) => (proj.hygiene.score > 0 ? [proj.id] : [])),
      ).size,
    [projects],
  );

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="large">
      <div
        style={{
          padding: '10px 14px',
          background: 'var(--ant-color-bg-container)',
          border: '1px solid var(--ant-color-border)',
          borderRadius: 8,
          fontSize: 13,
          color: 'var(--ant-color-text-secondary)',
        }}
      >
        <strong style={{ color: 'var(--ant-color-text)' }}>Hygiene.</strong>{' '}
        Data-quality and PM-discipline checks — independent from delivery
        health. {buckets.length} issue{' '}
        {buckets.length === 1 ? 'category' : 'categories'} across {totalFlagged}{' '}
        {totalFlagged === 1 ? 'project' : 'projects'}.
      </div>

      {buckets.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: 40,
            color: HEALTH_COLOR.green,
            fontSize: 16,
            fontWeight: 600,
          }}
        >
          No hygiene issues found.
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 14,
          }}
        >
          {buckets.map((bucket) => (
            <HygieneCard key={bucket.code} bucket={bucket} />
          ))}
        </div>
      )}

      {pmRows.length > 0 && (
        <IBCard isCovered title="PM Workload">
          <Table
            dataSource={pmRows}
            columns={pmColumns}
            rowKey="name"
            size="small"
            pagination={false}
          />
        </IBCard>
      )}
    </Space>
  );
};

// ── Layout ────────────────────────────────────────────────────────────────────

const ProjectsDashboardLayout = () => {
  const { loading, data } = useContext(ProjectsDashboardContext);
  const [selectedAM, setSelectedAM] = useState<string>('all');

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return <Alert type="error" message="Failed to load project health data." />;
  }

  const amOptions = [
    { value: 'all', label: 'All Account Managers' },
    ...Array.from(new Set(data.projects.map((proj) => proj.am || 'Unassigned')))
      .sort()
      .map((am) => ({ value: am, label: am })),
  ];

  const filteredProjects =
    selectedAM === 'all'
      ? data.projects
      : data.projects.filter(
          (proj) => (proj.am || 'Unassigned') === selectedAM,
        );

  const tabItems = [
    {
      key: 'summary',
      label: 'Summary',
      children: <SummaryTab projects={filteredProjects} />,
    },
    {
      key: 'projects',
      label: `Projects (${filteredProjects.length})`,
      children: <ProjectsTab projects={filteredProjects} />,
    },
    {
      key: 'hygiene',
      label: 'Hygiene',
      children: (
        <HygieneTab
          projects={filteredProjects}
          pmWorkload={data.pmWorkload}
          hygieneRollup={data.hygieneRollup}
        />
      ),
    },
  ];

  return (
    <ContentLayout>
      <IBCard
        isCovered
        title="Open Project Health"
        extra={
          <Select
            style={{ width: 220 }}
            options={amOptions}
            value={selectedAM}
            onChange={setSelectedAM}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        }
      >
        <Tabs items={tabItems} />
      </IBCard>
    </ContentLayout>
  );
};

export default ProjectsDashboardLayout;
